/**
 * Wrapper for course discussions.
 *
 * There are two types of course discussions pages:
 *   - questions
 *   - tips & thanks (known as "comments" in the code)
 *
 * These can be found at the following urls:
 *   - www.khanacademy.org/[domain_slug]/[course_slug]/d (questions)
 *   - www.khanacademy.org/[domain_slug]/[course_slug]/t (tips & thanks/comments)
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {compose} from "redux";

import {View} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";

import ErrorLoadingPage from "#app-shell-package/errors/error-loading-page.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import type {CurationData} from "#content-library-legacy/types.ts";
import withContentData, {
    type WithContentDataProps,
    type WithoutContentData,
} from "#content-library-legacy/with-content-data.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import type {GetTopicQuestionsQueryVariables} from "#discussion-package/graphql/__graphql-types__/GetTopicQuestionsQuery.ts";
import {GET_TOPIC_QUESTIONS_QUERY} from "#discussion-package/graphql/topic_questions_query.ts";
import type {FeedbackType} from "#discussion-package/types.ts";
import {filterFeedbackList} from "#discussion-package/util/feedback-filters.ts";

import CourseDiscussion from "./course-discussion.tsx";
import {withStrings} from "./locales/course-discussion-wrapper/load-strings.tsx";
import type {CourseInfo, DiscussionData} from "./types.ts";

type FetchedData = {
    data: DiscussionData;
    courseInfo: CourseInfo;
    domainId: string | null | undefined;
};

type Props = {
    // The domain slug is currently not being used.
    domain: string;
    course: string;
    type: "d" | "t";
} & WithApolloProps &
    WithContentDataProps<CurationData>;

type State = {
    fetchStatus: "unstarted" | "inProgress" | "done" | "failed";
    fetchedData: FetchedData | null | undefined;
};

class CourseDiscussionWrapper extends React.Component<Props, State> {
    state: State = {
        fetchStatus: "unstarted",
        fetchedData: null,
    };

    componentDidMount() {
        this._isMounted = true;
        if (this.props.contentData?.courseId) {
            this.fetchData();
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (
            prevProps.contentData?.courseId !== this.props.contentData?.courseId
        ) {
            this.fetchData();
        }
    }

    componentWillUnmount: () => void = () => {
        this._isMounted = false;
    };

    _isMounted = false;

    getFeedbackType: () => FeedbackType = () =>
        this.props.type === "d" ? "questions" : "comments";

    fetchData: () => void = () => {
        this.setState({fetchStatus: "inProgress"});

        const {course, gqlClient, contentData} = this.props;

        const queryParams: GetTopicQuestionsQueryVariables = {
            limit: 10,
            feedbackType:
                this.getFeedbackType() === "questions" ? "QUESTION" : "ANSWER",
            topicId: contentData?.courseId || "",
            cursor: this.state.fetchedData?.data.cursor,
        };
        gqlClient
            .query(GET_TOPIC_QUESTIONS_QUERY, {
                variables: queryParams,
                fetchPolicy: "network-only",
            })
            .then((response) => {
                if (!this._isMounted) {
                    return;
                }
                if (
                    response &&
                    response.data &&
                    response.data.topicQuestions &&
                    response.data.topicQuestions.topic
                ) {
                    const {complete, cursor, length, topic, domainId} =
                        response.data.topicQuestions;

                    const feedback =
                        response.data.topicQuestions.feedback ?? [];

                    const filteredFeedback = filterFeedbackList(feedback);
                    if (
                        topic.translatedTitle &&
                        topic.relativeUrl &&
                        topic.id
                    ) {
                        const {translatedTitle, relativeUrl, id} = topic;

                        const courseInfo = {
                            id: id,
                            slug: course,
                            title: translatedTitle,
                            url: relativeUrl,
                        } as const;
                        const data = {
                            complete: complete || false,
                            cursor: cursor || "",
                            length: length || 0,
                            topic: courseInfo,
                            feedback: filteredFeedback,
                        } as const;

                        this.setState({
                            fetchStatus: "done",
                            fetchedData: {
                                data,
                                courseInfo,
                                domainId,
                            },
                        });
                    }
                }
            })
            .catch(() => {
                if (this._isMounted) {
                    this.setState({fetchStatus: "failed"});
                }
            });
    };

    // We're passing in fetchedData instead of getting it from state to
    // convince TypeScript it's not null at this point.
    renderDiscussion: (fetchedData: FetchedData) => React.ReactNode = (
        fetchedData: FetchedData,
    ) => {
        const {data, courseInfo, domainId} = fetchedData;

        const discussion = {
            questionType: this.getFeedbackType(),
            data,
        } as const;

        const fullTitle = `${t`Discussion`} | ${courseInfo.title}`;
        const description = t`Discussion for ${courseInfo.title}`;

        return (
            <PageRoot
                settings={{
                    fullTitle,
                    description,
                    noIndex: true,
                    headerBackgroundColor: "darkBlue",
                }}
            >
                <View id="course-discussion-root">
                    <CourseDiscussion
                        discussion={discussion}
                        domainId={domainId}
                        topic={courseInfo}
                    />
                </View>
            </PageRoot>
        );
    };

    render(): React.ReactNode {
        const {fetchStatus, fetchedData} = this.state;

        if (fetchStatus === "unstarted" || fetchStatus === "inProgress") {
            return (
                <PageRoot
                    settings={{
                        fullTitle: t`Discussion`,
                        description: t`Discussion`,
                        headerBackgroundColor: "darkBlue",
                        noIndex: true,
                    }}
                >
                    <View id="course-discussion-root">
                        <View style={styles.spinnerContainer}>
                            <CircularSpinner size="large" />
                        </View>
                    </View>
                </PageRoot>
            );
        }

        if (fetchStatus === "failed" || !fetchedData) {
            return <ErrorLoadingPage />;
        }

        return this.renderDiscussion(fetchedData);
    }
}

const styles = StyleSheet.create({
    spinnerContainer: {
        margin: "0 auto",
        paddingTop: 120,
    },
});

type ExportProps = WithoutApollo<
    WithoutContentData<
        JSX.LibraryManagedAttributes<
            typeof CourseDiscussionWrapper,
            React.ComponentProps<typeof CourseDiscussionWrapper>
        >,
        CurationData
    >
>;

export default compose(
    withStrings,
    withContentData(),
    withApollo,
)(CourseDiscussionWrapper) as React.ComponentType<ExportProps>;
